import React, { useCallback, useEffect, useState } from 'react';
import {
  CLIENT_REPORTING_CHART_COLOR_FIELDS,
  DEFAULT_CLIENT_REPORTING_CHART_COLORS,
} from './clientReportingBenchmarkConfig';
import {
  fetchClientReportingChartColors,
  saveClientReportingChartColors,
} from '../../services/vectorDashboardApi';

function normalizeDraftColors(source = DEFAULT_CLIENT_REPORTING_CHART_COLORS) {
  return { ...DEFAULT_CLIENT_REPORTING_CHART_COLORS, ...source };
}

export default function ClientReportingChartColorsAdmin({ onSaved }) {
  const [draft, setDraft] = useState(DEFAULT_CLIENT_REPORTING_CHART_COLORS);
  const [saved, setSaved] = useState(DEFAULT_CLIENT_REPORTING_CHART_COLORS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchClientReportingChartColors()
      .then((res) => {
        if (cancelled || !res?.success) return;
        const colors = normalizeDraftColors(res.colors);
        setDraft(colors);
        setSaved(colors);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || 'Could not load chart colors.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  const updateColor = useCallback((key, value) => {
    setDraft((prev) => ({ ...prev, [key]: value }));
    setNotice('');
    setError('');
  }, []);

  const handleReset = useCallback(() => {
    setDraft({ ...DEFAULT_CLIENT_REPORTING_CHART_COLORS });
    setNotice('');
    setError('');
  }, []);

  const handleSave = useCallback(async () => {
    setSaving(true);
    setError('');
    setNotice('');
    try {
      const res = await saveClientReportingChartColors(draft);
      if (!res?.success) throw new Error(res?.error || 'Save failed');
      const colors = normalizeDraftColors(res.colors);
      setDraft(colors);
      setSaved(colors);
      setNotice('Chart colors saved. Everyone will see these on the next load.');
      onSaved?.(colors);
    } catch (err) {
      setError(err.message || 'Could not save chart colors.');
    } finally {
      setSaving(false);
    }
  }, [draft, onSaved]);

  const isDirty = JSON.stringify(draft) !== JSON.stringify(saved);

  return (
    <section className="vd-panel vd-cr-chart-colors-admin">
      <div className="vd-cr-chart-colors-admin-head">
        <div>
          <h3 className="vd-cr-chart-colors-admin-title">Chart line colors</h3>
          <p className="vd-cr-chart-colors-admin-copy">
            Global Portfolio Tracker (Broker Sync) palette. Account line color follows the broker
            you are viewing; benchmark colors are shared for everyone.
          </p>
        </div>
        <div className="vd-cr-chart-colors-admin-actions">
          <button type="button" className="vd-btn secondary" onClick={handleReset} disabled={saving || loading}>
            Reset defaults
          </button>
          <button
            type="button"
            className="vd-btn primary"
            onClick={handleSave}
            disabled={saving || loading || !isDirty}
          >
            {saving ? 'Saving…' : 'Save colors'}
          </button>
        </div>
      </div>

      {loading ? (
        <p className="vd-muted">Loading current colors…</p>
      ) : (
        <div className="vd-cr-chart-colors-grid">
          {CLIENT_REPORTING_CHART_COLOR_FIELDS.map(({ key, label, description }) => (
            <label className="vd-cr-chart-color-field" key={key}>
              <span className="vd-cr-chart-color-label">{label}</span>
              <span className="vd-cr-chart-color-description">{description}</span>
              <span className="vd-cr-chart-color-inputs">
                <input
                  type="color"
                  value={draft[key]}
                  onChange={(e) => updateColor(key, e.target.value)}
                  aria-label={`${label} color picker`}
                />
                <input
                  type="text"
                  value={draft[key]}
                  onChange={(e) => updateColor(key, e.target.value)}
                  spellCheck={false}
                  aria-label={`${label} hex value`}
                />
              </span>
            </label>
          ))}
        </div>
      )}

      {error ? <p className="vd-error">{error}</p> : null}
      {notice ? <p className="vd-notice">{notice}</p> : null}
    </section>
  );
}
